import { Injectable } from '@angular/core'
import { environment } from '@employer/environments/environment'
import { RestApiClient } from '@engineering11/web-api-rest'
import { EMPTY, Observable, of } from 'rxjs'
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators'
import { DEFAULT_NETWORK_MONITORING_CONFIG, getBaseSuffix, TokenStorage } from 'shared-lib'
import { IAssistantPendingAction, isPendingActionConfirmable } from '../models/assistant-pending-action.model'
import { AssistantPendingActionRepository } from '../repositories/assistant-pending-action.repository'
import { AssistantConversationService } from './assistant-conversation.service'

// BACKEND CONTRACT (assumption) — see ../BACKEND-CONTRACT.md
// Confirm-action route under `environment.services.assistant` (URI-versioned, id in path).
// Reconciled 2026-08-12: POST v1/pending-actions/:pendingActionId/confirm, body { confirmed }.
const confirmActionPath = (pendingActionId: string) => `pending-actions/${pendingActionId}/confirm`

/**
 * Raw `RestApiClient` errors are `E11ApiError` (an AxiosError whose `response.data` carries a
 * `statusCode`). `isE11ApiError` is not re-exported from `@engineering11/web-api-rest`'s public entry, so
 * we replicate its narrow inline rather than deep-import (mirrors `assistant-conversation.service.ts`).
 * A 409 is the busy/conflict signal (the action was already actioned/raced); we key on the status alone
 * (body `statusCode` OR the HTTP `response.status`) so a 409 whose body omits it is still swallowed.
 */
function isBusyConflict(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { statusCode?: number } } })?.response
  return response?.data?.statusCode === 409 || response?.status === 409
}

/**
 * Composes the live-listener repository into `pendingAction$` (keyed to the active conversation) and owns
 * the confirm seam (CAP-1/CAP-2/CAP-3). The card appears, resolves, and withdraws in place as the doc
 * updates. A confirm that races a busy backend (HTTP 409) is swallowed and never surfaced; any other
 * error propagates so the card shows its inline submit-failed state and re-enables Confirm.
 */
@Injectable({ providedIn: 'root' })
export class AssistantPendingActionService {
  private restClient: RestApiClient

  /**
   * The live pending-action document for the active conversation (or undefined when none). A listener
   * error (auth session expiry, permission-denied, datastore not-found — the SDK boundary this story
   * owns) degrades to `undefined` so the panel is unaffected (no card) rather than crashing the stream.
   */
  pendingAction$: Observable<IAssistantPendingAction | undefined> = this.conversationService.activeConversationId$.pipe(
    switchMap(id => (id ? this.repository.livePendingAction(id).pipe(catchError(() => of(undefined))) : of(undefined))),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  constructor(
    private repository: AssistantPendingActionRepository,
    private conversationService: AssistantConversationService,
    tokenStorage: TokenStorage
  ) {
    this.restClient = new RestApiClient({
      // Reconciled 2026-08-12: assistant is its own service (service-assistant at /assistant).
      baseUrl: environment.services.assistant,
      token: () => tokenStorage.getAccessToken(),
      baseSuffix: getBaseSuffix(environment, 'assistant'),
      ...DEFAULT_NETWORK_MONITORING_CONFIG,
    })
  }

  /**
   * Confirm a pending action. No-ops (`EMPTY`) unless a conversation is selected AND the action is still
   * confirmable (awaiting confirmation) — so a withdrawn/resolved action can never POST. On HTTP 409
   * (the action was already actioned/raced) the error is swallowed and the stream completes silently; the
   * card resolves via the listener (CAP-2). Any other error propagates so the card shows submit-failed.
   */
  confirm(action: IAssistantPendingAction): Observable<void> {
    const conversationId = this.conversationService.activeConversationId
    if (!conversationId || !isPendingActionConfirmable(action.status)) {
      return EMPTY
    }
    // Reconciled 2026-08-12: pendingActionId is a path param; body carries the confirm/decline verdict.
    return this.restClient.post<void>(confirmActionPath(action.id), { confirmed: true }).pipe(
      map(() => undefined),
      catchError(error => {
        if (isBusyConflict(error)) {
          return EMPTY
        }
        throw error
      })
    )
  }
}
